import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import GyongyFogaszat from './GyongyFogaszat';
import './App.css';

// Hooks
import { usePublicSettings } from './hooks/usePublicSettings';
import { useGA4 } from './hooks/useGA4';
import { useSearchInput } from './hooks/useSearchInput';

// Utils
import { formatCityDisplay, generateClinicSlug, parseClinicSlug } from './utils/helpers';

// Components
import ShareModal from './components/ShareModal';
import Footer from './components/Footer';

// Pages
import HomePage from './pages/HomePage';
import B2BLanding from './pages/B2BLanding';
import BlogList from './pages/BlogList';
import BlogDetail from './pages/BlogDetail';
import SearchResults from './pages/SearchResults';
import SitemapPage from './pages/SitemapPage';
import StaticListPage from './pages/StaticListPage';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AboutUs from './pages/AboutUs';
import ClinicDetail from './pages/ClinicDetail';

// Admin
import AdminPanel from './admin/AdminPanel';

function App() {
  // Public Settings and GA4 Analytics Hook
  const { settings: publicSettings, isGA4Loaded } = usePublicSettings();
  const { ga4TrackingId, trackEvent, trackPageView } = useGA4();
  
  const [clinics, setClinics] = useState([]);
  const [featuredClinics, setFeaturedClinics] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [searchFilters, setSearchFilters] = useState({
    city: '',
    county: '',
    district: '',
    services: [],
    freeText: '',
    nameSearch: '',
    openNow: false
  });
  const [municipalities, setMunicipalities] = useState([]);
  const [showMunicipalityDropdown, setShowMunicipalityDropdown] = useState(false);
  const [budapestDistricts, setBudapestDistricts] = useState([]);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [counties, setCounties] = useState([]);
  const [showServicesPanel, setShowServicesPanel] = useState(false);
  const servicesRef = useRef(null);
  const nativeInputRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setShowServicesPanel(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [services, setServices] = useState([]);
  const [currentView, setCurrentView] = useState('home');
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [selectedBlogPost, setSelectedBlogPost] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [displayedResults, setDisplayedResults] = useState([]);
  const [resultsPage, setResultsPage] = useState(1);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [similarPosts, setSimilarPosts] = useState([]);
  const resultsPerPage = 18;
  const [allBlogPosts, setAllBlogPosts] = useState([]);
  const [adminToken, setAdminToken] = useState(localStorage.getItem('adminToken'));
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  
  // Admin view state - lifted from AdminPanel to prevent re-mount issues
  const [adminView, setAdminView] = useState('dashboard');
  
  // Social media share modal
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [shareTitle, setShareTitle] = useState('');
  const [loginError, setLoginError] = useState('');
  const [sitemapData, setSitemapData] = useState({ cities: [], districts: [] });
  const [staticPageClinics, setStaticPageClinics] = useState([]);
  const [staticPageInfo, setStaticPageInfo] = useState({ city: '', district: '' });
  
  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const backendUrl = useMemo(() => process.env.REACT_APP_BACKEND_URL || '', []);

  // Social media share functions
  const openShareModal = (title, url = null) => {
    const currentUrl = url || window.location.href;
    setShareTitle(title);
    setShareUrl(currentUrl);
    setShowShareModal(true);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert('Link sikeresen vágólapra másolva!');
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        alert('Link sikeresen vágólapra másolva!');
      } catch (err) {
        alert('Link másolása sikertelen. Kérjük másolja ki manuálisan.');
      }
      document.body.removeChild(textArea);
    }
  };

  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareToX = () => {
    const text = `${shareTitle} - FogPortál.hu`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareToLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent(`Érdekes cikk a FogPortál.hu-ról: ${shareTitle}`);
    const body = encodeURIComponent(`Szia!\n\nÉrdekes fogászati cikket találtam a FogPortál.hu-n:\n\n${shareTitle}\n\n${shareUrl}\n\nÜdvözlettel!`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const loadClinic = async (clinicId) => {
    try {
      const response = await fetch(`${backendUrl}/api/clinics/${clinicId}`);
      const clinic = await response.json();
      setSelectedClinic(clinic);
    } catch (error) {
      console.error('Hiba a klinika betöltése során:', error);
    }
  };

  const loadClinicBySeoUrl = async (seoSlug) => {
    try {
      const parsed = parseClinicSlug(seoSlug);
      const params = new URLSearchParams();
      if (parsed.city) params.append('city', parsed.city);
      
      let nameToSearch = '';
      if (parsed.name) {
        nameToSearch = parsed.name.replace(/-/g, ' ').split(' ')[0];
        params.append('name_search', nameToSearch);
      }
      
      const response = await fetch(`${backendUrl}/api/clinics?${params}`);
      const clinics = await response.json();
      
      if (clinics.length > 0) {
        let bestMatch = clinics[0];
        
        if (parsed.district && clinics.length > 1) {
          const districtNumber = parsed.district.replace(/\D/g, '');
          const arabicToRoman = {
            '1': 'I', '2': 'II', '3': 'III', '4': 'IV', '5': 'V',
            '6': 'VI', '7': 'VII', '8': 'VIII', '9': 'IX', '10': 'X',
            '11': 'XI', '12': 'XII', '13': 'XIII', '14': 'XIV', '15': 'XV',
            '16': 'XVI', '17': 'XVII', '18': 'XVIII', '19': 'XIX', '20': 'XX',
            '21': 'XXI', '22': 'XXII', '23': 'XXIII'
          };
          
          const romanDistrict = arabicToRoman[districtNumber];
          if (romanDistrict) {
            const districtMatch = clinics.find(clinic => 
              clinic.district && clinic.district.includes(romanDistrict)
            );
            if (districtMatch) {
              bestMatch = districtMatch;
            }
          }
        }
        
        if (parsed.name && clinics.length > 1) {
          const nameToMatch = parsed.name.replace(/-/g, ' ').toLowerCase();
          const nameMatch = clinics.find(clinic => 
            clinic.name.toLowerCase().includes(nameToMatch)
          );
          if (nameMatch) {
            if (parsed.district) {
              const districtNumber = parsed.district.replace(/\D/g, '');
              const arabicToRoman = {
                '1': 'I', '2': 'II', '3': 'III', '4': 'IV', '5': 'V',
                '6': 'VI', '7': 'VII', '8': 'VIII', '9': 'IX', '10': 'X',
                '11': 'XI', '12': 'XII', '13': 'XIII', '14': 'XIV', '15': 'XV',
                '16': 'XVI', '17': 'XVII', '18': 'XVIII', '19': 'XIX', '20': 'XX',
                '21': 'XXI', '22': 'XXII', '23': 'XXIII'
              };
              const romanDistrict = arabicToRoman[districtNumber];
              
              if (romanDistrict && nameMatch.district && nameMatch.district.includes(romanDistrict)) {
                bestMatch = nameMatch;
              } else if (!bestMatch.district || !bestMatch.district.includes(romanDistrict)) {
                bestMatch = nameMatch;
              }
            } else {
              bestMatch = nameMatch;
            }
          }
        }
        
        setSelectedClinic(bestMatch);
        setCurrentView('clinic-detail');
      } else {
        console.error('Klinika nem található a SEO URL alapján:', seoSlug);
        setCurrentView('home');
      }
    } catch (error) {
      console.error('Hiba a klinika SEO URL betöltése során:', error);
      setCurrentView('home');
    }
  };

  // Initial route handling
  useEffect(() => {
    if (window.location.pathname === '/admin') {
      if (!adminToken) {
        setCurrentView('admin-login');
      } else {
        setCurrentView('admin-dashboard');
      }
    } else if (window.location.pathname === '/search') {
      const savedResults = sessionStorage.getItem('fogportal_search_results');
      const savedState = sessionStorage.getItem('fogportal_search_state');
      
      if (savedResults && savedState) {
        try {
          const resultsData = JSON.parse(savedResults);
          const stateData = JSON.parse(savedState);
          
          const searchAge = Date.now() - stateData.timestamp;
          if (searchAge < 3600000) {
            setSearchFilters(stateData.filters);
            setSearchResults(resultsData.results);
            setDisplayedResults(resultsData.displayedResults);
            setResultsPage(resultsData.resultsPage);
            setHasMoreResults(resultsData.hasMore);
            setCurrentView('search-results');
          } else {
            sessionStorage.removeItem('fogportal_search_results');
            sessionStorage.removeItem('fogportal_search_state');
            setCurrentView('home');
          }
        } catch (error) {
          console.error('Error restoring search state:', error);
          setCurrentView('home');
        }
      } else {
        setCurrentView('home');
      }
    } else if (window.location.pathname === '/blog') {
      setCurrentView('blog-list');
      loadAllBlogPosts();
    } else if (window.location.pathname.startsWith('/blog/')) {
      setCurrentView('blog-detail');
      const slugOrId = window.location.pathname.split('/blog/')[1];
      if (slugOrId) {
        loadBlogPost(slugOrId);
      }
    } else if (window.location.pathname.startsWith('/clinic/') || window.location.pathname.startsWith('/api/clinic/')) {
      setCurrentView('clinic-detail');
      const urlPath = window.location.pathname.includes('/api/clinic/') 
        ? window.location.pathname.split('/api/clinic/')[1]
        : window.location.pathname.split('/clinic/')[1];
      const urlParams = new URLSearchParams(window.location.search);
      const clinicId = urlParams.get('id');
      
      if (clinicId) {
        loadClinic(clinicId);
      } else if (urlPath && !urlPath.includes('-')) {
        loadClinic(urlPath);
      } else if (urlPath) {
        loadClinicBySeoUrl(urlPath);
      }
    } else if (window.location.pathname === '/megjelenes-fogaszatoknak') {
      setCurrentView('b2b-landing');
    } else if (window.location.pathname === '/gyongy-fogaszat-budapest-9-kerulet') {
      setCurrentView('gyongy-fogaszat');
    } else if (window.location.pathname === '/rolunk') {
      setCurrentView('about-us');
    } else if (window.location.pathname === '/adatvedelem') {
      setCurrentView('privacy-policy');
    } else if (window.location.pathname === '/aszf') {
      setCurrentView('terms-of-service');
    } else if (window.location.pathname === '/sitemap') {
      setCurrentView('sitemap');
      loadSitemapData();
    } else if (window.location.pathname.startsWith('/fogorvos-')) {
      setCurrentView('static-list');
      loadStaticPageData(window.location.pathname);
    } else {
      setCurrentView('home');
    }
    
    fetchInitialData();
  }, []);

  // Separate effect to handle admin token changes for admin routes only
  useEffect(() => {
    if (window.location.pathname === '/admin') {
      if (adminToken && currentView === 'admin-login') {
        setCurrentView('admin-dashboard');
      } else if (!adminToken && currentView !== 'admin-login') {
        setCurrentView('admin-login');
      }
    }
  }, [adminToken, currentView]);

  const loadAllBlogPosts = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/blog?limit=100`);
      const posts = await response.json();
      setAllBlogPosts(posts);
    } catch (error) {
      console.error('Hiba a blogbejegyzések betöltése során:', error);
    }
  };

  const loadBlogPost = async (slugOrId) => {
    try {
      let response;
      
      if (!slugOrId.includes('-') || slugOrId.length === 36) {
        response = await fetch(`${backendUrl}/api/blog/${slugOrId}`);
      } else {
        response = await fetch(`${backendUrl}/api/blog/slug/${slugOrId}`);
      }
      
      if (response.ok) {
        const post = await response.json();
        setSelectedBlogPost(post);
      } else {
        console.error('Hiba a blogbejegyzés betöltése során:', response.status);
        if (slugOrId.includes('-') && slugOrId.length !== 36) {
          try {
            const fallbackResponse = await fetch(`${backendUrl}/api/blog/${slugOrId}`);
            if (fallbackResponse.ok) {
              const post = await fallbackResponse.json();
              setSelectedBlogPost(post);
            }
          } catch (fallbackError) {
            console.error('Fallback load is also failed:', fallbackError);
          }
        }
      }
    } catch (error) {
      console.error('Hiba a blogbejegyzés betöltése során:', error);
    }
  };

  const loadSitemapData = async () => {
    try {
      const municipalitiesResponse = await fetch(`${backendUrl}/api/municipalities/autocomplete?limit=1000`);
      const municipalitiesData = await municipalitiesResponse.json();
      const citiesData = municipalitiesData.municipalities || [];

      const districtsResponse = await fetch(`${backendUrl}/api/budapest/districts`);
      const districtsData = await districtsResponse.json();
      const districtsList = districtsData.districts || [];

      setSitemapData({ cities: citiesData, districts: districtsList });
    } catch (error) {
      console.error('Hiba a sitemap adatok betöltése során:', error);
    }
  };

  const loadStaticPageData = async (pathname) => {
    try {
      const parsed = parseStaticPageUrl(pathname);
      setStaticPageInfo(parsed);

      const params = new URLSearchParams();
      
      if (parsed.city === 'Budapest' && parsed.district) {
        params.append('city', 'Budapest');
        params.append('district', parsed.district);
      } else if (parsed.city) {
        params.append('city', parsed.city);
        if (parsed.district) {
          params.append('district', parsed.district);
        }
      }
      
      const response = await fetch(`${backendUrl}/api/clinics?${params}`);
      const clinicsData = await response.json();
      
      setStaticPageClinics(clinicsData);
    } catch (error) {
      console.error('Hiba a statikus oldal adatok betöltése során:', error);
    }
  };

  const parseStaticPageUrl = (pathname) => {
    const decodedPathname = decodeURIComponent(pathname);
    const parts = decodedPathname.split('-');
    const parsed = { city: '', district: '' };

    if (parts.length >= 2) {
      const cleanCity = parts[1];
      
      const cityMapping = {
        'pecs': 'Pécs', 'gyor': 'Győr', 'miskolc': 'Miskolc', 
        'kecskemet': 'Kecskemét', 'budapest': 'Budapest', 'debrecen': 'Debrecen',
        'szeged': 'Szeged', 'nyiregyhaza': 'Nyíregyháza', 'szolnok': 'Szolnok',
        'eger': 'Eger', 'kaposvar': 'Kaposvár', 'zalaegerszeg': 'Zalaegerszeg',
        'veszprem': 'Veszprém', 'szombathely': 'Szombathely', 'tatabanya': 'Tatabánya'
      };
      
      parsed.city = cityMapping[cleanCity] || (cleanCity.charAt(0).toUpperCase() + cleanCity.slice(1));
      
      for (let i = 2; i < parts.length - 1; i++) {
        if (/^\d+$/.test(parts[i]) && parts[i + 1] === 'kerulet') {
          const arabicToRoman = {
            '1': 'I', '2': 'II', '3': 'III', '4': 'IV', '5': 'V',
            '6': 'VI', '7': 'VII', '8': 'VIII', '9': 'IX', '10': 'X',
            '11': 'XI', '12': 'XII', '13': 'XIII', '14': 'XIV', '15': 'XV',
            '16': 'XVI', '17': 'XVII', '18': 'XVIII', '19': 'XIX', '20': 'XX',
            '21': 'XXI', '22': 'XXII', '23': 'XXIII'
          };
          
          const romanNumeral = arabicToRoman[parts[i]];
          if (romanNumeral) {
            parsed.district = `${romanNumeral}. kerület`;
          } else {
            parsed.district = `${parts[i]}. kerület`;
          }
          break;
        }
      }
    }

    return parsed;
  };

  // Handle browser navigation (popstate)
  useEffect(() => {
    const handlePopState = (event) => {
      if (window.location.pathname === '/search' || 
          (event.state && event.state.searchResults)) {
        const savedResults = sessionStorage.getItem('fogportal_search_results');
        const savedState = sessionStorage.getItem('fogportal_search_state');
        
        if (savedResults && savedState) {
          try {
            const resultsData = JSON.parse(savedResults);
            const stateData = JSON.parse(savedState);
            
            setSearchFilters(stateData.filters);
            setSearchResults(resultsData.results);
            setDisplayedResults(resultsData.displayedResults);
            setResultsPage(resultsData.resultsPage);
            setHasMoreResults(resultsData.hasMore);
            setCurrentView('search-results');
            return;
          } catch (error) {
            console.error('Error restoring search state:', error);
          }
        }
        
        setCurrentView('home');
      } else if (window.location.pathname === '/admin') {
        if (!adminToken) {
          setCurrentView('admin-login');
        } else {
          setCurrentView('admin-dashboard');
        }
      } else if (window.location.pathname === '/blog') {
        setCurrentView('blog-list');
      } else if (window.location.pathname.startsWith('/blog/')) {
        setCurrentView('blog-detail');
        const slugOrId = window.location.pathname.split('/blog/')[1];
        if (slugOrId) {
          loadBlogPost(slugOrId);
        }
      } else if (window.location.pathname.startsWith('/clinic/')) {
        setCurrentView('clinic-detail');
        const urlPath = window.location.pathname.split('/clinic/')[1];
        const urlParams = new URLSearchParams(window.location.search);
        const clinicId = urlParams.get('id');
        
        if (clinicId) {
          loadClinic(clinicId);
        } else if (urlPath && !urlPath.includes('-')) {
          loadClinic(urlPath);
        } else if (urlPath) {
          loadClinicBySeoUrl(urlPath);
        }
      } else if (window.location.pathname === '/megjelenes-fogaszatoknak') {
        setCurrentView('b2b-landing');
      } else if (window.location.pathname === '/gyongy-fogaszat-budapest-9-kerulet') {
        setCurrentView('gyongy-fogaszat');
      } else if (window.location.pathname === '/rolunk') {
        setCurrentView('about-us');
      } else if (window.location.pathname === '/adatvedelem') {
        setCurrentView('privacy-policy');
      } else if (window.location.pathname === '/aszf') {
        setCurrentView('terms-of-service');
      } else if (window.location.pathname === '/sitemap') {
        setCurrentView('sitemap');
        loadSitemapData();
      } else if (window.location.pathname.startsWith('/fogorvos-')) {
        setCurrentView('static-list');
        loadStaticPageData(window.location.pathname);
      } else {
        setCurrentView('home');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const fetchInitialData = async () => {
    try {
      const featuredResponse = await fetch(`${backendUrl}/api/clinics?featured_only=true`);
      const featuredData = await featuredResponse.json();
      setFeaturedClinics(featuredData);

      const blogResponse = await fetch(`${backendUrl}/api/blog?limit=3`);
      const blogData = await blogResponse.json();
      setBlogPosts(blogData);

      const partnersResponse = await fetch(`${backendUrl}/api/partners`);
      const partnersData = await partnersResponse.json();
      const activePartners = partnersData.filter(partner => partner.active);
      setPartners(activePartners);

      const servicesResponse = await fetch(`${backendUrl}/api/services`);
      const servicesData = await servicesResponse.json();
      setServices(servicesData.services || []);

      const districtsResponse = await fetch(`${backendUrl}/api/budapest/districts`);
      const districtsData = await districtsResponse.json();
      setBudapestDistricts(districtsData.districts || []);

      const countiesResponse = await fetch(`${backendUrl}/api/counties`);
      const countiesData = await countiesResponse.json();
      const countyNames = countiesData.map(county => county.name).sort();
      setCounties(countyNames);
    } catch (error) {
      console.error('Hiba az adatok betöltése során:', error);
    }
  };

  // Memoized callback to prevent SearchInput re-renders
  const handleCitySelect = useCallback((cityName) => {
    setSearchFilters(prev => ({ 
      ...prev, 
      city: cityName,
      district: '' 
    }));
  }, []);

  const toggleService = (service) => {
    setSearchFilters(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleAdvancedSearch = async () => {
    try {
      const params = new URLSearchParams();
      const cityValue = searchFilters.city.trim();
      
      const searchState = {
        filters: searchFilters,
        timestamp: Date.now()
      };
      sessionStorage.setItem('fogportal_search_state', JSON.stringify(searchState));
      
      if (cityValue) {
        if (cityValue.toLowerCase() === 'budapest') {
          const response = await fetch(`${backendUrl}/api/cities/search-with-budapest-logic?city=budapest`);
          const data = await response.json();
          setSearchResults(data.clinics || []);
          setDisplayedResults((data.clinics || []).slice(0, resultsPerPage));
          setResultsPage(1);
          setHasMoreResults((data.clinics || []).length > resultsPerPage);
          setCurrentView('search-results');
          
          sessionStorage.setItem('fogportal_search_results', JSON.stringify({
            results: data.clinics || [],
            displayedResults: (data.clinics || []).slice(0, resultsPerPage),
            resultsPage: 1,
            hasMore: (data.clinics || []).length > resultsPerPage,
            searchType: 'budapest'
          }));
          
          window.history.pushState({ searchResults: true }, '', '/search');
          
          if (isGA4Loaded) {
            trackEvent('search', {
              search_term: cityValue,
              search_type: 'budapest_comprehensive_search',
              result_count: data.clinics?.length || 0
            });
          }
          return;
        } else {
          params.append('city', cityValue);
        }
      }
      
      if (searchFilters.county && !cityValue) {
        params.append('county', searchFilters.county);
      }
      
      if (searchFilters.district) params.append('district', searchFilters.district);
      
      if (searchFilters.services.length > 0) {
        params.append('services', searchFilters.services.join(','));
      }
      
      if (searchFilters.freeText) params.append('free_text', searchFilters.freeText);
      if (searchFilters.nameSearch) params.append('name_search', searchFilters.nameSearch);
      if (searchFilters.openNow) params.append('open_now', 'true');

      const response = await fetch(`${backendUrl}/api/clinics?${params}`);
      const data = await response.json();
      setSearchResults(data);
      setDisplayedResults(data.slice(0, resultsPerPage));
      setResultsPage(1);
      setHasMoreResults(data.length > resultsPerPage);
      setCurrentView('search-results');
      
      sessionStorage.setItem('fogportal_search_results', JSON.stringify({
        results: data,
        displayedResults: data.slice(0, resultsPerPage),
        resultsPage: 1,
        hasMore: data.length > resultsPerPage,
        searchType: 'advanced'
      }));
      
      window.history.pushState({ searchResults: true }, '', '/search');

      if (isGA4Loaded) {
        trackEvent('search', {
          search_term: searchFilters.nameSearch || searchFilters.city || 'empty',
          search_type: 'clinic_search',
          results_count: data.length
        });
      }
    } catch (error) {
      console.error('Hiba a keresés során:', error);
    }
  };

  const loadMoreResults = () => {
    const nextPage = resultsPage + 1;
    const startIndex = nextPage * resultsPerPage;
    const endIndex = startIndex + resultsPerPage;
    const newResults = searchResults.slice(0, endIndex);
    
    setDisplayedResults(newResults);
    setResultsPage(nextPage);
    setHasMoreResults(searchResults.length > endIndex);
  };

  const viewClinic = async (clinicId, openInNewTab = false) => {
    try {
      const response = await fetch(`${backendUrl}/api/clinics/${clinicId}`);
      const clinic = await response.json();
      
      if (clinic.has_subpage && clinic.subpage_url) {
        window.open(clinic.subpage_url, '_blank');
        return;
      }
      
      const seoSlug = generateClinicSlug(clinic);
      const baseUrl = window.location.origin;
      const clinicUrl = `${baseUrl}/clinic/${seoSlug}`;
      
      if (openInNewTab) {
        window.open(clinicUrl, '_blank');
      } else {
        setSelectedClinic(clinic);
        setCurrentView('clinic-detail');
        
        const isFromSearchResults = currentView === 'search-results' || sessionStorage.getItem('fogportal_search_state');
        
        window.history.pushState({ 
          fromSearch: isFromSearchResults,
          clinicId: clinic.id 
        }, '', `/clinic/${seoSlug}`);
        
        if (isGA4Loaded) {
          trackEvent('view_item', {
            item_id: clinic.id,
            item_name: clinic.name,
            item_category: 'clinic',
            item_list_name: 'clinic_search_results'
          });
        }
      }
    } catch (error) {
      console.error('Hiba a klinika betöltése során:', error);
    }
  };

  const viewBlogPost = async (postId, postSlug = null) => {
    try {
      let response;
      let finalSlug = postSlug;
      
      if (postSlug) {
        response = await fetch(`${backendUrl}/api/blog/slug/${postSlug}`);
      } else {
        response = await fetch(`${backendUrl}/api/blog/${postId}`);
        if (response.ok) {
          const tempPost = await response.json();
          finalSlug = tempPost.slug;
        }
      }
      
      if (response.ok) {
        const post = await response.json();
        setSelectedBlogPost(post);
        setCurrentView('blog-detail');
        
        const urlSlug = finalSlug || postId;
        window.history.pushState(null, '', `/blog/${urlSlug}`);
      } else {
        console.error('Hiba a blogbejegyzés betöltése során:', response.status);
      }
    } catch (error) {
      console.error('Hiba a blogbejegyzés betöltése során:', error);
    }
  };

  const navigateToBlog = () => {
    if (isGA4Loaded) {
      trackEvent('page_view', {
        page_title: 'Blog',
        page_location: window.location.origin + '/blog'
      });
    }
    window.location.href = '/blog';
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });

      if (response.ok) {
        const data = await response.json();
        setAdminToken(data.access_token);
        localStorage.setItem('adminToken', data.access_token);
        setCurrentView('admin-dashboard');
        window.history.pushState(null, '', '/admin');
      } else {
        const error = await response.json();
        setLoginError(error.detail || 'Bejelentkezési hiba');
      }
    } catch (error) {
      setLoginError('Hálózati hiba történt');
    }
  };

  const handleAdminLogout = () => {
    setAdminToken(null);
    localStorage.removeItem('adminToken');
    setCurrentView('home');
    window.history.pushState(null, '', '/');
  };

  const navigateToTerms = () => { window.location.href = '/aszf'; };
  const navigateToPrivacy = () => { window.location.href = '/adatvedelem'; };
  const navigateToAbout = () => { window.location.href = '/rolunk'; };

  const navigateToHome = () => {
    sessionStorage.removeItem('fogportal_search_results');
    sessionStorage.removeItem('fogportal_search_state');
    if (isGA4Loaded) {
      trackEvent('page_view', {
        page_title: 'FogPortál.hu - Főoldal',
        page_location: window.location.origin + '/'
      });
    }
    window.location.href = '/';
  };

  const navigateToB2B = () => {
    if (isGA4Loaded) {
      trackEvent('page_view', {
        page_title: 'Megjelenés fogászatoknak',
        page_location: window.location.origin + '/megjelenes-fogaszatoknak'
      });
    }
    window.location.href = '/megjelenes-fogaszatoknak';
  };

  const navigateToSitemap = () => { window.location.href = '/sitemap'; };

  // Common navigation props
  const navProps = {
    navigateToHome, navigateToBlog, navigateToB2B,
    navigateToPrivacy, navigateToTerms, navigateToAbout, navigateToSitemap
  };

  // Share modal props
  const shareModalProps = {
    showShareModal, setShowShareModal, shareUrl, shareTitle,
    copyToClipboard, shareToFacebook, shareToX, shareToLinkedIn, shareViaEmail
  };

  // Render current view
  if (currentView === 'admin-login') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-blue-600 mb-2">FogPortál.hu</h1>
            <h2 className="text-xl font-semibold text-gray-800">Admin Bejelentkezés</h2>
          </div>
          
          <form onSubmit={handleAdminLogin} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email cím</label>
              <input
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData(prev => ({...prev, email: e.target.value}))}
                className="form-input"
                placeholder="admin@fogportal.hu"
                required
                data-testid="admin-email-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Jelszó</label>
              <input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData(prev => ({...prev, password: e.target.value}))}
                className="form-input"
                placeholder="........"
                required
                data-testid="admin-password-input"
              />
            </div>
            
            {loginError && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded" data-testid="login-error">
                {loginError}
              </div>
            )}
            
            <button type="submit" className="w-full primary-button" data-testid="admin-login-button">
              Bejelentkezés
            </button>
          </form>
          
          <div className="mt-6 text-center">
            <button 
              onClick={navigateToHome}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              Vissza a főoldalra
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  if (adminToken && (currentView === 'admin-dashboard' || currentView === 'admin-login')) {
    return <AdminPanel adminView={adminView} setAdminView={setAdminView} adminToken={adminToken} backendUrl={backendUrl} handleAdminLogout={handleAdminLogout} />;
  }
  
  if (currentView === 'b2b-landing') {
    return <B2BLanding {...navProps} backendUrl={backendUrl} />;
  }
  
  if (currentView === 'terms-of-service') {
    return <TermsOfService {...navProps} backendUrl={backendUrl} />;
  }

  if (currentView === 'privacy-policy') {
    return <PrivacyPolicy {...navProps} backendUrl={backendUrl} />;
  }

  if (currentView === 'about-us') {
    return <AboutUs {...navProps} backendUrl={backendUrl} publicSettings={publicSettings} />;
  }

  if (currentView === 'gyongy-fogaszat') {
    return <GyongyFogaszat />;
  }
  
  if (currentView === 'blog-list') {
    return <BlogList {...navProps} allBlogPosts={allBlogPosts} onViewBlogPost={viewBlogPost} backendUrl={backendUrl} />;
  }
  
  if (currentView === 'blog-detail' && selectedBlogPost) {
    return (
      <div>
        <BlogDetail 
          post={selectedBlogPost} 
          {...navProps}
          openShareModal={openShareModal}
          backendUrl={backendUrl}
          similarPosts={similarPosts}
          onViewBlogPost={viewBlogPost}
        />
        <ShareModal {...shareModalProps} />
      </div>
    );
  }
  
  if (currentView === 'search-results') {
    return <SearchResults 
      {...navProps}
      searchResults={searchResults}
      displayedResults={displayedResults}
      hasMoreResults={hasMoreResults}
      loadMoreResults={loadMoreResults}
      isMobileMenuOpen={isMobileMenuOpen}
      setIsMobileMenuOpen={setIsMobileMenuOpen}
      onViewClinic={viewClinic}
    />;
  }
  
  if (currentView === 'clinic-detail' && selectedClinic) {
    return <ClinicDetail 
      clinic={selectedClinic}
      backendUrl={backendUrl}
      {...navProps}
      onViewClinic={viewClinic}
      openShareModal={openShareModal}
      isMobileMenuOpen={isMobileMenuOpen}
      setIsMobileMenuOpen={setIsMobileMenuOpen}
      {...shareModalProps}
      publicSettings={publicSettings}
      searchFilters={searchFilters}
      setSearchFilters={setSearchFilters}
      handleCitySelect={handleCitySelect}
      handleAdvancedSearch={handleAdvancedSearch}
      showAdvancedSearch={showAdvancedSearch}
      setShowAdvancedSearch={setShowAdvancedSearch}
      counties={counties}
      budapestDistricts={budapestDistricts}
      services={services}
      showServicesPanel={showServicesPanel}
      setShowServicesPanel={setShowServicesPanel}
      servicesRef={servicesRef}
      toggleService={toggleService}
      onBackToSearch={() => {
        const savedResults = sessionStorage.getItem('fogportal_search_results');
        const savedState = sessionStorage.getItem('fogportal_search_state');
        if (savedResults && savedState) {
          try {
            const resultsData = JSON.parse(savedResults);
            const stateData = JSON.parse(savedState);
            setSearchFilters(stateData.filters);
            setSearchResults(resultsData.results);
            setDisplayedResults(resultsData.displayedResults);
            setResultsPage(resultsData.resultsPage);
            setHasMoreResults(resultsData.hasMore);
            setCurrentView('search-results');
            window.history.pushState({ searchResults: true }, '', '/search');
          } catch (error) {
            navigateToHome();
          }
        } else {
          navigateToHome();
        }
      }}
    />;
  }

  if (currentView === 'sitemap') {
    return <SitemapPage {...navProps} sitemapData={sitemapData} />;
  }

  if (currentView === 'static-list') {
    return <StaticListPage {...navProps} staticPageClinics={staticPageClinics} staticPageInfo={staticPageInfo} onViewClinic={viewClinic} />;
  }

  return (
    <div>
      <HomePage 
        {...navProps}
        publicSettings={publicSettings}
        backendUrl={backendUrl}
        searchFilters={searchFilters}
        setSearchFilters={setSearchFilters}
        municipalities={municipalities}
        showMunicipalityDropdown={showMunicipalityDropdown}
        setShowMunicipalityDropdown={setShowMunicipalityDropdown}
        budapestDistricts={budapestDistricts}
        districts={districts}
        services={services}
        counties={counties}
        handleAdvancedSearch={handleAdvancedSearch}
        showAdvancedSearch={showAdvancedSearch}
        setShowAdvancedSearch={setShowAdvancedSearch}
        showServicesPanel={showServicesPanel}
        setShowServicesPanel={setShowServicesPanel}
        servicesRef={servicesRef}
        nativeInputRef={nativeInputRef}
        featuredClinics={featuredClinics}
        partners={partners}
        blogPosts={blogPosts}
        onViewClinic={viewClinic}
        onViewBlogPost={viewBlogPost}
        toggleService={toggleService}
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        handleCitySelect={handleCitySelect}
        isGA4Loaded={isGA4Loaded}
        trackEvent={trackEvent}
      />
      <ShareModal {...shareModalProps} />
    </div>
  );
}

export default App;
